<template>
  <div v-if="list">
    <section class="hero-slab">
      <div class="hero-slab__media" :style="{ backgroundImage: 'url(' + list.heroImage + ')' }"></div>
      <div class="hero-slab__overlay"></div>
      <div class="hero-slab__content container reveal">
        <div class="section-title">
          <p>{{ list.group }}</p>
          <h1>{{ list.title }}</h1>
        </div>
        <p>{{ list.intro }}</p>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.products'), to: '/products' },
        { label: list.group, to: list.group === 'COLOURS' ? '/colours' : '/products' },
        { label: list.title, to: $route.path }
      ]"
      :back-label="$t('common.backToProducts')"
      back-to="/products"
    />

    <section class="section-spacing light-panel">
      <div class="container reveal">
        <div class="product-list-grid">
          <router-link
            v-for="item in products"
            :key="item.code"
            class="product-list-card"
            :to="'/products-page/' + (item.slug || item.code.toLowerCase())"
          >
            <div class="product-list-card__image">
              <img :src="item.image" :alt="item.title" loading="lazy">
            </div>
            <div class="product-list-card__body">
              <h3>{{ item.code }}</h3>
            </div>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'
import { fetchPublicCategories, fetchPublicProducts, normalizeProduct, resolveAssetUrl } from '../api/public'

export default {
  name: 'ProductListPage',
  components: {
    PageTrail
  },
  data () {
    return {
      backendList: null,
      backendProducts: []
    }
  },
  computed: {
    list () {
      if (this.backendList) return this.backendList
      return site.productLists[this.$route.params.slug] || null
    },
    products () {
      if (this.backendProducts.length) return this.backendProducts
      if (this.backendList) return []
      if (!this.list) return []
      return this.list.products.map(slug => site.productItems[slug]).filter(Boolean)
    }
  },
  watch: {
    '$route.params.slug': {
      immediate: true,
      handler () {
        this.loadBackendData()
      }
    }
  },
  methods: {
    loadBackendData () {
      this.backendList = null
      this.backendProducts = []

      Promise.all([
        fetchPublicCategories(),
        fetchPublicProducts({ categorySlug: this.$route.params.slug })
      ])
        .then(([categories, products]) => {
          const category = categories.find(item => item.slug === this.$route.params.slug)

          if (category) {
            const mockList = site.productLists[this.$route.params.slug]
          this.backendList = {
            group: category.type === 'colour' ? 'COLOURS' : 'NATURAL WOOD FLOORS',
            title: category.name,
            intro: category.description || (mockList ? mockList.intro : ''),
            heroImage: resolveAssetUrl(category.image, mockList ? mockList.heroImage : site.hero.background)
          }
          }

          this.backendProducts = products.map(item => normalizeProduct(item, site.hero.background))
        })
        .catch(() => {
          this.backendList = null
          this.backendProducts = []
        })
        .then(() => {
          if (!this.list) {
            this.$router.replace('/products')
          }
        })
    }
  }
}
</script>

<style>
.product-list-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 28px 22px;
}

.product-list-card {
  display: block;
  border: 1px solid rgba(23, 20, 17, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.58);
  color: inherit;
  box-shadow: 0 18px 34px rgba(23, 20, 17, 0.05);
  transition: transform 0.24s ease, box-shadow 0.24s ease;
}

.product-list-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 42px rgba(23, 20, 17, 0.08);
}

.product-list-card__image {
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f2eee8;
}

.product-list-card__image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.product-list-card:hover .product-list-card__image img {
  transform: scale(1.06);
}

.product-list-card__body {
  padding: 14px 14px 14px;
}

.product-list-card h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.24;
  letter-spacing: 0.03em;
}

@media (max-width: 1100px) {
  .product-list-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .product-list-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }
}

@media (max-width: 540px) {
  .product-list-grid {
    grid-template-columns: 1fr;
  }

  .product-list-card__body {
    padding: 13px 12px;
  }

  .product-list-card h3 {
    font-size: 18px;
  }
}
</style>
